import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePageView } from "src/hooks/use-page-view";
import { Seo } from "src/components/seo";
import { usePathname } from "src/hooks/use-pathname";

interface TestPeriod {
  name: string;
  stopEating: string;
  eatingAndBolusing: string;
  evaluateDataFrom: string;
  okayToEatAfter: string;
  changeBasal: string;
  hours: number;
}

interface BasalAdjustment {
  bloodSugarChange: string;
  changeType: "modest" | "large";
  range1: string; // 0.0 - 0.35
  range2: string; // 0.4 - 1.0
  range3: string; // >1.0
}

const testPeriods: TestPeriod[] = [
  {
    name: "Overnight",
    stopEating: "19:00",
    eatingAndBolusing: "Eat dinner around 18:00 and skip evening snacks",
    evaluateDataFrom: "23:00 - 07:00",
    okayToEatAfter: "7:00",
    changeBasal: "21:00 - 05:00",
    hours: 8,
  },
  {
    name: "Morning",
    stopEating: "3:00",
    eatingAndBolusing: "Bedtime snack and skip breakfast",
    evaluateDataFrom: "07:00 - 13:00",
    okayToEatAfter: "13:00",
    changeBasal: "05:00 - 11:00",
    hours: 6,
  },
  {
    name: "Afternoon",
    stopEating: "9:00",
    eatingAndBolusing: "Eat breakfast, skip lunch and afternoon snacks",
    evaluateDataFrom: "13:00 - 18:00",
    okayToEatAfter: "18:00",
    changeBasal: "11:00 - 16:00",
    hours: 5,
  },
  {
    name: "Evening",
    stopEating: "14:00",
    eatingAndBolusing:
      "Have a late lunch, skip afternoon snacks and have late dinner",
    evaluateDataFrom: "18:00 - 23:00",
    okayToEatAfter: "23:00",
    changeBasal: "16:00 - 21:00",
    hours: 5,
  },
];

const basalAdjustments: BasalAdjustment[] = [
  {
    bloodSugarChange: "1.7 - 3.4 mmol/L",
    changeType: "modest",
    range1: "0.025 - 0.05",
    range2: "0.10",
    range3: "0.2",
  },
  {
    bloodSugarChange: ">3.4 mmol/L",
    changeType: "large",
    range1: "0.05 - 0.10",
    range2: "0.20",
    range3: "0.3 - 0.4",
  },
];

const Page = () => {
  const { t } = useTranslation();
  usePageView();
  const theme = useTheme();
  const pathname = usePathname();
  // Check if we're in dashboard layout (pathname includes /dashboard)
  const isDashboard = pathname?.includes("/dashboard") ?? false;

  const [selectedTestPeriod, setSelectedTestPeriod] = useState<string>("");
  const [currentBasal, setCurrentBasal] = useState<string>("");
  const [initialGlucose, setInitialGlucose] = useState<string>("");
  const [finalGlucose, setFinalGlucose] = useState<string>("");
  const [calculatedChange, setCalculatedChange] = useState<string | null>(null);
  const [calculationDetails, setCalculationDetails] = useState<string>("");

  const calculateBasalChange = () => {
    if (
      !selectedTestPeriod ||
      !currentBasal ||
      !initialGlucose ||
      !finalGlucose
    ) {
      alert(t("Please fill in all fields"));
      return;
    }

    const initial = parseFloat(initialGlucose);
    const final = parseFloat(finalGlucose);
    const basal = parseFloat(currentBasal);

    if (isNaN(initial) || isNaN(final) || isNaN(basal)) {
      alert(t("Please enter valid numbers"));
      return;
    }

    const glucoseChange = final - initial;
    const glucoseChangeAbs = Math.abs(glucoseChange);
    const isModest = glucoseChangeAbs >= 1.7 && glucoseChangeAbs <= 3.4;
    const isLarge = glucoseChangeAbs > 3.4;
    const glucoseIncreased = glucoseChange > 0;
    const glucoseDecreased = glucoseChange < 0;

    if (!isModest && !isLarge) {
      setCalculatedChange(t("No change needed"));
      setCalculationDetails(
        t(
          "Glucose change of {{value}} mmol/L is less than 1.7 mmol/L. No basal adjustment recommended.",
          { value: glucoseChangeAbs.toFixed(1) }
        )
      );
      return;
    }

    // Determine which range the current basal falls into
    let adjustment: string;
    let range: string;

    if (basal >= 0 && basal <= 0.35) {
      range = "0.0 - 0.35";
      if (isModest) {
        adjustment = basalAdjustments[0].range1;
      } else {
        adjustment = basalAdjustments[1].range1;
      }
    } else if (basal >= 0.4 && basal <= 1.0) {
      range = "0.4 - 1.0";
      if (isModest) {
        adjustment = basalAdjustments[0].range2;
      } else {
        adjustment = basalAdjustments[1].range2;
      }
    } else {
      range = ">1.0";
      if (isModest) {
        adjustment = basalAdjustments[0].range3;
      } else {
        adjustment = basalAdjustments[1].range3;
      }
    }

    const testPeriod = testPeriods.find((tp) => tp.name === selectedTestPeriod);
    const changeType = isModest ? t("Modest") : t("Large");

    // Determine direction: if glucose increased, increase basal; if decreased, decrease basal
    const direction = glucoseIncreased
      ? t("Increase")
      : glucoseDecreased
      ? t("Decrease")
      : t("Adjust");
    const directionSymbol = glucoseIncreased
      ? "+"
      : glucoseDecreased
      ? "-"
      : "±";

    setCalculatedChange(`${directionSymbol}${adjustment}`);
    const note = glucoseIncreased
      ? t("Since glucose increased during the test, increase your basal rate.")
      : glucoseDecreased
      ? t("Since glucose decreased during the test, decrease your basal rate.")
      : t("No significant change detected.");
    setCalculationDetails(
      [
        t("For {{period}} test period:", { period: t(selectedTestPeriod) }),
        t("- Initial glucose: {{value}} mmol/L", {
          value: initial.toFixed(1),
        }),
        t("- Final glucose: {{value}} mmol/L", { value: final.toFixed(1) }),
        t("- Glucose change: {{value}} mmol/L ({{type}})", {
          value: `${glucoseChange > 0 ? "+" : ""}${glucoseChange.toFixed(1)}`,
          type: changeType,
        }),
        t("- Current basal: {{basal}} units/hour (Range: {{range}})", {
          basal,
          range,
        }),
        t("- Recommended basal change: {{direction}} by {{adjustment}} units/hour", {
          direction,
          adjustment,
        }),
        t("- Apply change during: {{window}}", {
          window: testPeriod?.changeBasal || t("N/A"),
        }),
        "",
        t("Note: {{note}}", { note }),
      ].join("\n")
    );
  };

  const mdUp = useMediaQuery(theme.breakpoints.up("md"));

  return (
    <>
      <Seo title="Basal Testing Calculator" />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: isDashboard ? 2 : 0,
        }}
      >
        <Box
          sx={{
            backgroundColor: isDashboard ? "transparent" : "#f5f5f5",
            pt: isDashboard ? 0 : "120px", // Add top padding for fixed nav in marketing layout
            pb: isDashboard ? 0 : mdUp ? "100px" : "60px",
            minHeight: isDashboard ? "auto" : "100vh",
          }}
        >
          <Container maxWidth={isDashboard ? "xl" : "lg"}>
            <Stack spacing={4}>
              <Box
                sx={{
                  mb: isDashboard ? 4 : 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: isDashboard ? "flex-start" : "center",
                }}
              >
                <Typography
                  variant={isDashboard ? "h4" : "h3"}
                  sx={
                    isDashboard
                      ? {}
                      : {
                          textAlign: "center",
                          fontWeight: "bold",
                          m: 2,
                          color: theme.palette.primary.main,
                        }
                  }
                  gutterBottom={isDashboard}
                >
                  {t("Basal Testing Calculator")}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    textAlign: isDashboard ? "left" : "center",
                    maxWidth: isDashboard ? "none" : 800,
                  }}
                >
                  {t("Calculator to determine appropriate basal rate changes.")}
                </Typography>

              </Box>

              {/* Test Periods Table */}
              <Card>
                <CardContent>
                  <Typography variant="h5" sx={{ mb: 2 }}>
                    {t("Basal Testing Schedule")}
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700 }}>{t("Test")}</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            {t("Stop eating and bolusing")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            {t("Eating and bolusing")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            {t("Evaluate data from")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            {t("Okay to eat and bolus again after")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            {t("Change basal")}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            {t("Number of hours")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {testPeriods.map((period) => (
                          <TableRow key={period.name}>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {t(period.name)}
                            </TableCell>
                            <TableCell>{period.stopEating}</TableCell>
                            <TableCell>{t(period.eatingAndBolusing)}</TableCell>
                            <TableCell>{period.evaluateDataFrom}</TableCell>
                            <TableCell>{period.okayToEatAfter}</TableCell>
                            <TableCell>{period.changeBasal}</TableCell>
                            <TableCell>{period.hours}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* Basal Adjustment Table */}
              <Card>
                <CardContent>
                  <Typography variant="h5" sx={{ mb: 2 }}>
                    {t("Basal Adjustment Recommendations")}
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            rowSpan={2}
                            sx={{ fontWeight: 700, verticalAlign: "top" }}
                          >
                            {t("Blood sugar change during test")}
                          </TableCell>
                          <TableCell
                            colSpan={3}
                            align="center"
                            sx={{ fontWeight: 700 }}
                          >
                            {t("Current basal level (units/hour)")}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>
                            0.0 - 0.35
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            0.4 - 1.0
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            &gt;1.0
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {basalAdjustments.map((adjustment, index) => (
                          <TableRow key={index}>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {adjustment.bloodSugarChange ===
                              "1.7 - 3.4 mmol/L"
                                ? t("Modest")
                                : t("Large")}
                              <br />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {adjustment.bloodSugarChange}
                              </Typography>
                            </TableCell>
                            <TableCell>{adjustment.range1}</TableCell>
                            <TableCell>{adjustment.range2}</TableCell>
                            <TableCell>{adjustment.range3}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* Calculator */}
              <Card>
                <CardContent>
                  <Typography variant="h5" sx={{ mb: 3 }}>
                    {t("Basal Change Calculator")}
                  </Typography>
                  <Stack spacing={3}>
                    <FormControl fullWidth>
                      <InputLabel>{t("Test Period")}</InputLabel>
                      <Select
                        value={selectedTestPeriod}
                        label={t("Test Period")}
                        onChange={(e) => setSelectedTestPeriod(e.target.value)}
                      >
                        {testPeriods.map((period) => (
                          <MenuItem key={period.name} value={period.name}>
                            {t(period.name)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      label={t("Current Basal Level (units/hour)")}
                      type="number"
                      value={currentBasal}
                      onChange={(e) => setCurrentBasal(e.target.value)}
                      inputProps={{ step: "0.01", min: "0" }}
                      helperText={t(
                        "Enter your current basal rate in units per hour"
                      )}
                    />

                    <TextField
                      fullWidth
                      label={t("Initial Glucose Level (mmol/L)")}
                      type="number"
                      value={initialGlucose}
                      onChange={(e) => setInitialGlucose(e.target.value)}
                      inputProps={{ step: "0.1", min: "0" }}
                      helperText={t(
                        "Enter glucose level at the start of the test period"
                      )}
                    />

                    <TextField
                      fullWidth
                      label={t("Final Glucose Level (mmol/L)")}
                      type="number"
                      value={finalGlucose}
                      onChange={(e) => setFinalGlucose(e.target.value)}
                      inputProps={{ step: "0.1", min: "0" }}
                      helperText={t(
                        "Enter glucose level at the end of the test period"
                      )}
                    />

                    <Button
                      variant="contained"
                      size="large"
                      onClick={calculateBasalChange}
                      sx={{ mt: 2 }}
                    >
                      {t("Calculate Basal Change")}
                    </Button>

                    {calculatedChange && (
                      <Box
                        sx={{
                          mt: 3,
                          p: 3,
                          backgroundColor: (theme) =>
                            theme.palette.mode === "dark"
                              ? "neutral.700"
                              : "primary.50",
                          borderRadius: 2,
                        }}
                      >
                        <Typography variant="h6" sx={{ mb: 2 }}>
                          {t("Recommended Basal Change")}
                        </Typography>
                        <Typography
                          variant="h4"
                          color="primary"
                          sx={{ fontWeight: "bold", mb: 2 }}
                        >
                          {t("{{change}} units/hour", { change: calculatedChange })}
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        <Typography
                          variant="body1"
                          sx={{ whiteSpace: "pre-line" }}
                        >
                          {calculationDetails}
                        </Typography>
                      </Box>
                    )}
                                    <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    maxWidth: isDashboard ? "none" : 800,
                  }}
                >
                  {t("Calculations are based on the book")}{" "}
                  <a
                    href="https://integrateddiabetes.com/store/think-like-a-pancreas-4th-edition"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#1976d2", textDecoration: "underline" }}
                  >
                    &quot;Think Like a Pancreas&quot;
                  </a>
                  {" "}
                  {t("by Gary Scheiner")}
                </Typography>
                
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Container>
        </Box>
        {!isDashboard && <Divider />}
      </Box>
    </>
  );
};

export default Page;
